import { cn } from '@gettactic/helpers';
import { createMapLink } from '@gettactic/helpers';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@v2/components/ui/tooltip';
import { Button } from '@cmp-src/buttons';
import { ViewDeskIcon } from '@cmp-src/icons/TacticIcons/VIewDeskIcon';
import { WorkingLocationPresenceIcon } from './WorkingLocationPresenceIcon';
import { IResource, IWorkingLocation, IUserFull } from '@gettactic/api';

import { useRouter } from 'next/router';
import { AvailableStatus } from '@cmp-src/icons/TacticIcons/statusIcons';

interface CellProps {
  status: string;
  resource?: IResource;
  officeName?: string;
  officeId?: string;
  date: string;
  startTime?: string;
  endTime?: string;
}

export interface ExtendedUser {
  user: IUserFull;
  week: Record<string, IWorkingLocation>;
}

export const OfficeStatusCell = ({
  status,
  resource,
  officeName,
  officeId,
  date,
  startTime,
  endTime
}: CellProps) => {
  const router = useRouter();
  const hasDesk = status === 'inOffice';
  const isRemote = status === AvailableStatus.remote;
  const isOOO = status === AvailableStatus.ooo;
  const isUnknown = status === AvailableStatus.unknown;
  const notAnOffice = isRemote || isOOO || isUnknown;

  const label = hasDesk
    ? null
    : isRemote
      ? 'Working from home'
      : isOOO
        ? 'Out of office'
        : 'Not specified';

  const link = createMapLink({
    date,
    officeId,
    resourceId: resource?.id,
    floorId: resource?.parent_id ?? undefined,
    startTime,
    endTime,
  });

  return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            className={cn(
              'dashboard-status-cell',
              '!p-0 !rounded-v2 m-auto',
              'flex h-[36px] bg-neutral hover:bg-neutral-100',
              'w-full max-w-full items-center justify-center align-center',
              hasDesk ? 'text-primary' : 'text-disabled',
              officeName ? 'cursor-pointer' : 'cursor-default'
            )}
            onClick={() => {
              officeName && router.push(link);
            }}
            variant={'custom'}
          >
            <WorkingLocationPresenceIcon
              className="h-[13px] w-[13px]"
              status={status}
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className={
            'flex min-w-[7rem] justify-center gap-0.5 rounded-lg bg-hint-bg px-2.5 py-1.5 text-hint-text text-xs shadow-xl'
          }
        >
          {notAnOffice ? (
            <LabelWrapper>{label}</LabelWrapper>
          ) : (
            <LabelWrapper
              onClick={() => {
                officeName && router.push(link);
              }}
            >
              <div className="flex items-center gap-0.5 text-xs">
                {officeName || 'Office'} {hasDesk && '-'}{' '}
                {hasDesk && !!resource && (resource?.name ?? 'Resource')}
              </div>
            </LabelWrapper>
          )}
        </TooltipContent>
      </Tooltip>
  );
};

const LabelWrapper = ({
  children,
  onClick
}: {
  children: React.ReactNode;
  onClick?: () => void;
}) => {
  return (
    <div
      className={cn(
        'flex items-center gap-0.5 align-center align-items text-xs',
        !!onClick && 'cursor-pointer'
      )}
      onClick={onClick}
    >
      {children}
      {!!onClick && <ViewDeskIcon className="h-4 w-4" />}
    </div>
  );
};
